'use client';

import React from 'react';

const STORAGE_KEY = 'c3d_configurator_experience';

export type ConfiguratorExperienceMode = 'free' | 'guided';

export function readStoredExperienceMode(): ConfiguratorExperienceMode | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    if (v === 'free' || v === 'guided') return v;
  } catch {
    /* ignore */
  }
  return null;
}

export function persistExperienceMode(mode: ConfiguratorExperienceMode): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

/**
 * Shown when scene metadata `configuratorExperience` is `ask` and the user has not chosen yet this session.
 */
export function ConfiguratorModeIntro({
  onChoose,
}: {
  onChoose: (mode: ConfiguratorExperienceMode) => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-5 py-10 bg-zinc-950/75 backdrop-blur-xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="c3d-intro-title"
    >
      <div className="c3d-intro-panel w-full max-w-lg">
        <p className="text-center text-[10px] font-medium uppercase tracking-[0.35em] text-white/35">
          Configuration
        </p>
        <h2
          id="c3d-intro-title"
          className="mt-4 text-center text-2xl font-light tracking-tight text-white sm:text-3xl"
          style={{ fontFamily: 'ui-serif, Georgia, "Times New Roman", serif' }}
        >
          How would you like to proceed?
        </h2>
        <p className="mx-auto mt-3 max-w-sm text-center text-sm font-light leading-relaxed text-white/45">
          Browse every option at once, or move through each choice in order.
        </p>
        <div className="mt-10 grid gap-3 sm:grid-cols-2 sm:gap-4">
          <button
            type="button"
            onClick={() => onChoose('free')}
            className="group rounded-2xl px-6 py-8 text-left transition-all duration-300 hover:bg-white/[0.06] active:scale-[0.99]"
          >
            <span className="block text-[11px] font-medium uppercase tracking-[0.2em] text-white/40 transition-colors group-hover:text-white/55">
              Free
            </span>
            <span
              className="mt-2 block text-lg font-light text-white/90 transition-colors group-hover:text-white"
              style={{ fontFamily: 'ui-serif, Georgia, serif' }}
            >
              Explore all options
            </span>
            <span className="mt-2 block text-xs font-light leading-relaxed text-white/35">
              See every attribute at a glance. Change anything, anytime.
            </span>
          </button>
          <button
            type="button"
            onClick={() => onChoose('guided')}
            className="group rounded-2xl px-6 py-8 text-left transition-all duration-300 hover:bg-white/[0.06] active:scale-[0.99]"
          >
            <span className="block text-[11px] font-medium uppercase tracking-[0.2em] text-white/40 transition-colors group-hover:text-white/55">
              Guided
            </span>
            <span
              className="mt-2 block text-lg font-light text-white/90 transition-colors group-hover:text-white"
              style={{ fontFamily: 'ui-serif, Georgia, serif' }}
            >
              Step by step
            </span>
            <span className="mt-2 block text-xs font-light leading-relaxed text-white/35">
              Focus one choice at a time with clear next and previous steps.
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
